import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight, Sparkles, Check, X, Wallet, BadgeIndianRupee, TrendingUp,
    Footprints, Shirt, ShoppingCart, Scissors, Smartphone, Package, Truck,
    UtensilsCrossed, Stethoscope, GraduationCap, ShoppingBag, Building2,
    PhoneCall, MessageSquareText, CalendarCheck2, CircleDashed,
    Target, Eye, HeartHandshake, Star, MapPin,
} from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Tilt3D, PhoneMockup, LaptopMockup } from "../components/DeviceMockups";
import { SERVICE_VISUALS } from "../components/Robot";
import { VeerVisual, SanaVisual, SupportVisual, AutomationVisual } from "../components/Agents";
import { MediaCoverage } from "../components/MediaCoverage";
import { WhatsAppIcon } from "../components/BrandIcons";
import { COMPANY, PRODUCTS, STEPS, INDUSTRIES, PLANS } from "../lib/content";
import { LeadForm } from "../components/LeadForm";

const CATEGORY_ICONS = {
    shoe: Footprints, shirt: Shirt, cart: ShoppingCart, scissors: Scissors, phone: Smartphone,
    package: Package, truck: Truck, food: UtensilsCrossed, stethoscope: Stethoscope,
    graduation: GraduationCap, bag: ShoppingBag, building: Building2,
};

const FEATURED_REVIEWS = [
    {
        name: "Priya Verma", city: "Ahmedabad", biz: "Boutique", gradient: "from-cyan-500 to-blue-600",
        text: "WhatsApp પર ગ્રાહકોના જવાબ હવે AI તરત આપે છે. અમારી દુકાનની inquiry એક પણ miss થતી નથી.",
        translation: "AI now replies to customers instantly on WhatsApp. Not a single enquiry from our shop gets missed.",
    },
    {
        name: "Krishna Iyer", city: "Chennai", biz: "Electronics Retail", gradient: "from-amber-500 to-orange-600",
        text: "இரவு நேரத்திலும் விசாரணைகள் தவறவில்லை. முன்பை விட முன்பதிவுகள் அதிகரித்துள்ளன.",
        translation: "No enquiry is missed even at night. Bookings have increased compared to before.",
    },
    {
        name: "Neha Agarwal", city: "Delhi", biz: "Beauty Salon", gradient: "from-pink-500 to-rose-600",
        text: "Follow-ups used to be our weakest point. Now the AI chases every lead politely until they book. Our business finally runs on autopilot.",
        translation: "",
    },
];

const PRODUCT_VISUALS = { whatsapp: SanaVisual, calling: VeerVisual, autopilot: AutomationVisual };

const AGENT_BADGES = {
    whatsapp: { label: "Sana", color: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
    calling: { label: "Veer", color: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10" },
    autopilot: { label: "Veer + Sana", color: "text-blue-300 border-blue-500/30 bg-blue-500/10" },
};

const SERVICES = [
    { id: "calling", title: "AI Calling Agent", desc: "Natural, multilingual AI calls that qualify leads, follow up and book meetings — with live call analytics.", accent: "text-cyan-400" },
    { id: "whatsapp", title: "WhatsApp AI Agent", desc: "Instant, accurate replies on WhatsApp — 24/7, in your customer's language, straight to a booked appointment.", accent: "text-emerald-400" },
    { id: "support", title: "Customer Support", desc: "Every customer query answered in seconds — FAQs, ticket resolution and status updates without wait times.", accent: "text-sky-400" },
    { id: "crm", title: "CRM Assistance", desc: "Every conversation and lead auto-organised in one pipeline — nothing slips through the cracks.", accent: "text-violet-400" },
    { id: "automation", title: "Workflow Automation", desc: "Follow-ups, reminders and bookings run on autopilot — your business keeps moving while you sleep.", accent: "text-teal-400" },
];

const serviceVisual = (id) =>
    ({ calling: VeerVisual, whatsapp: SanaVisual, support: SupportVisual, automation: AutomationVisual }[id] ||
    SERVICE_VISUALS[id]);

const SHOP_PROBLEMS = [
    { icon: CircleDashed, title: "Busy at the shop?", desc: "AI handles your customers' calls and routine enquiries — even while you're serving another customer." },
    { icon: X, title: "Shop closed for the day?", desc: "AI stays available around the clock — answering customer questions and saving their details." },
    { icon: BadgeIndianRupee, title: "Constant price questions?", desc: "AI answers instantly, based on your own product and price list." },
    { icon: PhoneCall, title: "Missing customer calls?", desc: "On the connected calling system, AI can handle every customer call for you." },
    { icon: MessageSquareText, title: "Flooded with WhatsApp messages?", desc: "AI helps with routine questions, product information and customer support." },
    { icon: CalendarCheck2, title: "No budget for a receptionist?", desc: "AI collects customer details, books appointments and sends the important leads to you." },
];

const HIGHLIGHTS = [
    { icon: X, title: "No Monthly Bills", desc: "No mandatory payment every month — recharge only when you need to.", cls: "bg-red-500/10 text-red-400" },
    { icon: Wallet, title: "Pay Only When Used", desc: "Balance is deducted only on real AI calling or messaging usage.", cls: "bg-emerald-500/10 text-emerald-400" },
    { icon: TrendingUp, title: "Start Small, Grow Big", desc: "Start with just ₹499 — recharge more as your business grows.", cls: "bg-cyan-500/10 text-cyan-400" },
];

const USAGE_ROWS = [
    { label: "AI Call — New enquiry (2 min)", amount: "-₹19.90", time: "Yesterday, 4:12 PM" },
    { label: "WhatsApp — Product question", amount: "-₹0.99", time: "Yesterday, 1:05 PM" },
    { label: "Recharge — Option A", amount: "+₹499.00", time: "26 Oct 2026", credit: true },
];

const DEMO_CATEGORIES = [
    {
        id: "shoe", label: "Shoe Shop",
        veerQ: "Hello, is your shop open?", veerA: "Namaste! Our shop is closed right now, but I can still help you. Which product would you like to know about?",
        sanaQ: "Can I get sports shoes under ₹2,000?", sanaA: "Yes sir! I can help you find options within your budget from our catalogue. Would you like to see the collection? ✓✓",
    },
    {
        id: "clothing", label: "Clothing Store",
        veerQ: "Do you have cotton kurtis in size M?", veerA: "Yes! We have cotton kurtis in size M starting at ₹599. Shall I book a time for you to visit the store?",
        sanaQ: "Any discount on festive wear?", sanaA: "Yes! Our festive collection has offers this week. I can share the catalogue and reserve your size. ✓✓",
    },
    {
        id: "grocery", label: "Grocery Shop",
        veerQ: "Do you deliver groceries at home?", veerA: "Yes, we deliver within 5 km. You can share your list on WhatsApp and I'll confirm the total and delivery time.",
        sanaQ: "What is the price of 5 kg atta?", sanaA: "5 kg atta is ₹240 today. Would you like me to add it to your order for home delivery? ✓✓",
    },
    {
        id: "salon", label: "Salon",
        veerQ: "Can I book a haircut for tomorrow?", veerA: "Of course! Tomorrow we have slots at 11 AM and 4 PM. Which time works for you? I'll confirm your booking right away.",
        sanaQ: "What are your facial prices?", sanaA: "Our facials start at ₹499. I can share the full price list and book your slot. ✓✓",
    },
    {
        id: "restaurant", label: "Restaurant",
        veerQ: "I want to book a table for 4 tonight.", veerA: "Sure! A table for 4 is available at 8 PM tonight. I've noted your booking — anything special you'd like arranged?",
        sanaQ: "Do you have Jain food options?", sanaA: "Yes! We have a full Jain menu. I can share it here and book your table too. ✓✓",
    },
    {
        id: "supplier", label: "Supplier",
        veerQ: "What is your wholesale rate for this item?", veerA: "Thank you for your enquiry! Wholesale rates depend on quantity. May I know your approximate monthly requirement? I'll have our team share the best rate.",
        sanaQ: "Can you send your wholesale price list?", sanaA: "Yes! Please share your business name and city — I'll send the wholesale catalogue and connect you with our sales team. ✓✓",
    },
    {
        id: "realestate", label: "Real Estate",
        veerQ: "I'm looking for a 2BHK flat.", veerA: "Great! May I know your preferred location and budget? I'll shortlist matching properties and schedule a site visit for you.",
        sanaQ: "Any 2BHK under ₹40 lakh?", sanaA: "Yes, we have 3 options in your budget. I can share photos, location details and book a site visit. ✓✓",
    },
];

export default function Home() {
    const bgVideoRef = useRef(null);
    const [demoCat, setDemoCat] = useState("shoe");
    const demo = DEMO_CATEGORIES.find((c) => c.id === demoCat);

    useEffect(() => {
        const v = bgVideoRef.current;
        if (v) v.play().catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* ============ HERO ============ */}
            <section className="relative overflow-hidden">
                <video
                    ref={bgVideoRef}
                    data-testid="hero-bg-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/assets/video-poster.jpg"
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover opacity-15"
                >
                    <source src="/assets/workassign-bg.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-[#080C14]/80" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#080C14]/60 via-transparent to-[#080C14]" />
                <div className="glow-orb left-[-10%] top-[-10%] h-[420px] w-[420px] bg-blue-600/20" />
                <div className="glow-orb right-[-5%] top-[30%] h-[380px] w-[380px] bg-cyan-500/15" />
                <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:pt-24">
                    <div>
                        <Reveal>
                            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-500/10 px-4 py-1.5 text-xs font-semibold text-cyan-300">
                                <Sparkles className="h-3.5 w-3.5" /> {COMPANY.legal}
                            </span>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.12] tracking-tight text-white [text-shadow:0_2px_24px_rgba(2,6,17,0.9)] sm:text-5xl lg:text-[3.4rem]" data-testid="hero-heading">
                                Your business may close. <span className="cyan-gradient-text">Your AI employee never does.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <p className="mt-4 font-display text-xl font-bold text-amber-300 sm:text-2xl" data-testid="hero-subheading">
                                24×7 AI Receptionist, Calling & WhatsApp Automation — starting at just ₹499.
                            </p>
                        </Reveal>
                        <Reveal delay={0.26}>
                            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
                                You handle your business — your AI handles your customers. When you're busy, the shop is
                                closed, or you're unavailable, WorkAssign AI answers calls, replies on WhatsApp, shares
                                product details and handles new enquiries.{" "}
                                <span className="font-semibold text-slate-200">No Enquiry. No Usage Charge. No Monthly Bills.</span>
                            </p>
                        </Reveal>
                        <Reveal delay={0.34}>
                            <div className="mt-8 flex flex-wrap items-center gap-4">
                                <Link
                                    to="/pricing"
                                    data-testid="hero-cta-primary"
                                    className="flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-7 py-3.5 text-sm font-semibold text-white shadow-xl shadow-cyan-500/25 transition-transform hover:scale-[1.04]"
                                >
                                    Start with ₹499 <ArrowRight className="h-4 w-4" />
                                </Link>
                                <a
                                    href="#demo"
                                    data-testid="hero-cta-demo"
                                    className="flex items-center gap-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 px-7 py-3.5 text-sm font-semibold text-cyan-300 backdrop-blur transition-colors hover:bg-cyan-500/20"
                                >
                                    <PhoneCall className="h-4 w-4" /> Talk to Veer
                                </a>
                                <a
                                    href="#demo"
                                    data-testid="hero-cta-sana"
                                    className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-7 py-3.5 text-sm font-semibold text-emerald-300 backdrop-blur transition-colors hover:bg-emerald-500/20"
                                >
                                    <WhatsAppIcon className="h-4 w-4" /> Chat with Sana
                                </a>
                                <a href="#plans" data-testid="hero-cta-plans" className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-500/50 hover:text-cyan-300">
                                    Explore Plans
                                </a>
                            </div>
                        </Reveal>
                        <Reveal delay={0.42}>
                            <div className="mt-9 flex flex-wrap gap-2.5">
                                {["No Monthly Bills", "24×7 AI", "Pay Only for Usage", "Veer + Sana"].map((c) => (
                                    <span key={c} className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-slate-300">
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>

                    <div className="relative hidden justify-center lg:flex" data-testid="hero-3d-showcase">
                        <Tilt3D intensity={9}>
                            <div className="relative preserve-3d">
                                <LaptopMockup />
                                <motion.div
                                    className="absolute -top-32 -left-10 z-20 w-[270px]"
                                    style={{ transform: "translateZ(120px)" }}
                                    initial={{ opacity: 0, y: -30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5, duration: 0.8 }}
                                    data-testid="hero-agents"
                                >
                                    <img
                                        src="/assets/veer-sana.png"
                                        alt="Veer and Sana — your AI team"
                                        className="animate-floaty w-full"
                                        style={{ filter: "drop-shadow(0 26px 34px rgba(2,6,17,0.65))" }}
                                    />
                                    <div className="mt-1 flex gap-2">
                                        <span className="glass flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold text-cyan-300 notranslate">
                                            Veer · Calling AI
                                            <span className="flex items-end gap-[2px] h-3">
                                                {[0, 1, 2].map((i) => (
                                                    <span key={i} className="wave-bar w-[2px] rounded-full bg-cyan-300" style={{ height: "100%", animationDelay: `${i * 0.15}s` }} />
                                                ))}
                                            </span>
                                        </span>
                                        <span className="glass flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold text-emerald-300 notranslate">
                                            Sana · WhatsApp AI <span className="text-cyan-300">✓✓</span>
                                        </span>
                                    </div>
                                </motion.div>
                                <motion.div
                                    className="absolute -right-16 top-16 animate-floaty"
                                    style={{ transform: "translateZ(70px)" }}
                                    initial={{ opacity: 0, x: 40 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.4, duration: 0.8 }}
                                >
                                    <PhoneMockup />
                                </motion.div>
                                <motion.div
                                    className="absolute -left-10 -top-6 glass animate-floaty rounded-2xl px-4 py-3"
                                    style={{ transform: "translateZ(90px)", animationDelay: "1.2s" }}
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.8 }}
                                >
                                    <p className="font-mono2 text-[10px] uppercase tracking-wider text-slate-400">Wallet</p>
                                    <p className="font-display text-sm font-bold text-emerald-400">No usage today · ₹0 charge</p>
                                </motion.div>
                                <motion.div
                                    className="absolute -bottom-8 left-8 glass animate-floaty rounded-2xl px-4 py-3"
                                    style={{ transform: "translateZ(80px)", animationDelay: "2s" }}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1 }}
                                >
                                    <p className="font-mono2 text-[10px] uppercase tracking-wider text-slate-400">Next Action</p>
                                    <p className="font-display text-sm font-bold text-cyan-300">Demo booked · 4:30 PM</p>
                                </motion.div>
                            </div>
                        </Tilt3D>
                    </div>
                </div>
            </section>

            {/* ============ ABOUT US ============ */}
            <section className="relative py-20 lg:py-28" data-testid="about-section">
                <div className="glow-orb left-[10%] top-[10%] h-[320px] w-[420px] bg-blue-600/15" />
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                    <Reveal>
                        <Eyebrow>About Us</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="about-heading">
                            AI employees for every business — <span className="cyan-gradient-text">built in India</span>
                        </h2>
                        <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
                            {COMPANY.legal} builds AI employees that call, chat, manage, follow up and automate —
                            so businesses grow without growing headcount. From your first AI receptionist to your
                            complete AI workforce.
                        </p>
                        <Link
                            to="/company"
                            data-testid="about-read-more"
                            className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 transition-colors hover:text-cyan-300"
                        >
                            Know our story <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { v: "24×7", l: "AI on duty" },
                                { v: "₹499", l: "Starting price" },
                                { v: "48+", l: "Media publications" },
                                { v: "1,248+", l: "Verified reviews" },
                            ].map((s) => (
                                <div key={s.l} className="glow-card rounded-2xl p-6 text-center">
                                    <p className="font-display text-3xl font-extrabold text-white">{s.v}</p>
                                    <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{s.l}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ============ MISSION & VISION ============ */}
            <section className="relative py-20 lg:py-28" data-testid="mission-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>Mission & Vision</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="mission-heading">
                            Why we <span className="cyan-gradient-text">exist</span>
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-6 md:grid-cols-3">
                        {[
                            { icon: Target, title: "Mission", desc: "To give every business — from local shops to enterprises — an AI workforce that converts conversations into growth." },
                            { icon: Eye, title: "Vision", desc: "A future where no customer enquiry in India goes unanswered, and no business loses growth to manual follow-up." },
                            { icon: HeartHandshake, title: "Values", desc: "Transparency, reliability and real business outcomes. We build AI that works for people, not instead of them." },
                        ].map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.1}>
                                <div className="glow-card h-full rounded-2xl p-7" data-testid={`mission-card-${i}`}>
                                    <div className="mb-4 inline-flex rounded-xl bg-cyan-500/10 p-3 text-cyan-400">
                                        <v.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="font-display text-lg font-bold text-white">{v.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ SHOP PROBLEMS → AI SOLUTIONS ============ */}
            <section className="relative py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>Problems → AI Solutions</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="problem-heading">
                            You can't be available all the time. <span className="cyan-gradient-text">Your AI can.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {SHOP_PROBLEMS.map((c, i) => (
                            <Reveal key={c.title} delay={(i % 3) * 0.1}>
                                <div className="glow-card h-full rounded-2xl p-6" data-testid={`problem-card-${i}`}>
                                    <div className="mb-4 inline-flex rounded-xl bg-cyan-500/10 p-3 text-cyan-400">
                                        <c.icon className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-lg font-bold text-white">{c.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ PRODUCTS — VEER & SANA ============ */}
            <section className="relative py-20 lg:py-28">
                <div className="glow-orb left-[20%] top-0 h-[300px] w-[500px] bg-blue-600/10" />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>The AI Workforce</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="products-heading">
                            Meet <span className="text-cyan-400">Veer</span> & <span className="text-emerald-400">Sana</span> — your <span className="cyan-gradient-text">AI employees</span>
                        </h2>
                        <p className="mx-auto mt-4 max-w-2xl text-slate-400">
                            Not another chatbot. Digital employees designed to perform real business tasks.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid gap-6 lg:grid-cols-3">
                        {PRODUCTS.map((p, i) => {
                            const Visual = PRODUCT_VISUALS[p.id];
                            const badge = AGENT_BADGES[p.id];
                            return (
                                <Reveal key={p.id} delay={i * 0.12}>
                                    <div className="glow-card flex h-full flex-col overflow-hidden rounded-2xl" data-testid={`product-card-${p.id}`}>
                                        <div className="relative h-48 border-b border-white/5 bg-[#0A1020]/60 p-2">
                                            <Visual compact />
                                            <span className={`absolute left-3 top-3 rounded-full border px-3 py-1 text-[10px] font-bold notranslate ${badge.color}`}>
                                                {badge.label}
                                            </span>
                                        </div>
                                        <div className="flex flex-1 flex-col p-7">
                                            <h3 className="font-display text-xl font-bold text-white">{p.name}</h3>
                                            <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                                            <ul className="mt-5 flex-1 space-y-2">
                                                {p.features.map((f) => (
                                                    <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                                                        <Check className={`h-3.5 w-3.5 ${p.accent}`} /> {f}
                                                    </li>
                                                ))}
                                            </ul>
                                            <Link
                                                to="/contact"
                                                data-testid={`product-cta-${p.id}`}
                                                className={`mt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${p.accent}`}
                                            >
                                                Explore {p.name} <ArrowRight className="h-4 w-4" />
                                            </Link>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============ SERVICES ============ */}
            <section className="relative py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>In Action</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="services-heading">
                            One AI workforce. <span className="cyan-gradient-text">Six superpowers.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {SERVICES.map((s, i) => {
                            const Visual = serviceVisual(s.id);
                            return (
                                <Reveal key={s.id} delay={(i % 3) * 0.1}>
                                    <div className="glow-card group h-full rounded-2xl p-6" data-testid={`service-${s.id}`}>
                                        <div className="h-48 transition-transform duration-500 group-hover:scale-[1.04]">
                                            <Visual />
                                        </div>
                                        <h3 className={`mt-4 font-display text-lg font-bold ${s.accent}`}>{s.title}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============ LIVE AI DEMO ============ */}
            <section id="demo" className="relative scroll-mt-20 py-20 lg:py-28 grid-bg">
                <div className="glow-orb right-[10%] top-[5%] h-[320px] w-[420px] bg-cyan-500/10" />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>Live AI Demo</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="demo-heading">
                            Watch your AI employee <span className="cyan-gradient-text">answer customers</span>
                        </h2>
                        <p className="mx-auto mt-4 max-w-2xl text-sm text-slate-400">
                            Sample conversations — real answers come from your connected product data and business information.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-2.5" data-testid="demo-categories">
                            {DEMO_CATEGORIES.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setDemoCat(c.id)}
                                    data-testid={`demo-cat-${c.id}`}
                                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                                        demoCat === c.id
                                            ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200"
                                            : "border-white/10 bg-white/5 text-slate-400 hover:border-cyan-500/30 hover:text-slate-200"
                                    }`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                    <div className="mt-14 grid gap-6 lg:grid-cols-2">
                        <Reveal>
                            <div className="glow-card h-full rounded-3xl p-6 sm:p-8" data-testid="demo-veer">
                                <div className="flex items-center gap-4">
                                    <img src="/assets/veer.png" alt="Veer — AI Calling Agent" className="animate-floaty h-20 w-auto object-contain" />
                                    <div>
                                        <p className="font-display text-lg font-bold text-cyan-300 notranslate">Veer — AI Calling Agent</p>
                                        <p className="flex items-center gap-1.5 text-xs text-emerald-400">
                                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> On a live customer call
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-6 space-y-3">
                                    <div className="rounded-2xl rounded-tl-sm bg-white/5 px-4 py-3 text-sm text-slate-300">
                                        <span className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500">Customer</span>
                                        <p className="mt-1" data-testid="demo-veer-customer">"{demo.veerQ}"</p>
                                    </div>
                                    <div className="rounded-2xl rounded-tr-sm border border-cyan-500/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">
                                        <span className="font-mono2 text-[10px] uppercase tracking-wider text-cyan-400 notranslate">Veer</span>
                                        <p className="mt-1" data-testid="demo-veer-reply">"{demo.veerA}"</p>
                                    </div>
                                </div>
                                <a
                                    href={COMPANY.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="demo-talk-veer"
                                    className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
                                >
                                    <PhoneCall className="h-4 w-4" /> Talk to Veer
                                </a>
                            </div>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="glow-card h-full rounded-3xl p-6 sm:p-8" data-testid="demo-sana">
                                <div className="flex items-center gap-4">
                                    <img src="/assets/sana.png" alt="Sana — AI WhatsApp Agent" className="animate-floaty h-20 w-auto object-contain" style={{ animationDelay: "1s" }} />
                                    <div>
                                        <p className="font-display text-lg font-bold text-emerald-300 notranslate">Sana — AI WhatsApp Agent</p>
                                        <p className="flex items-center gap-1.5 text-xs text-emerald-400">
                                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Replying on WhatsApp
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-6 space-y-3">
                                    <div className="rounded-2xl rounded-tl-sm bg-[#1F2C34] px-4 py-3 text-sm text-slate-300">
                                        <span className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500">Customer</span>
                                        <p className="mt-1" data-testid="demo-sana-customer">"{demo.sanaQ}"</p>
                                    </div>
                                    <div className="rounded-2xl rounded-tr-sm bg-[#005C4B]/60 px-4 py-3 text-sm text-emerald-50">
                                        <span className="font-mono2 text-[10px] uppercase tracking-wider text-emerald-300 notranslate">Sana</span>
                                        <p className="mt-1" data-testid="demo-sana-reply">"{demo.sanaA}"</p>
                                    </div>
                                </div>
                                <a
                                    href={COMPANY.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="demo-chat-sana"
                                    className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-500 px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
                                >
                                    <WhatsAppIcon className="h-4 w-4" color="#fff" /> Chat with Sana
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ BUSINESS CATEGORIES ============ */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>Business Categories</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="industries-heading">
                            One AI employee. <span className="cyan-gradient-text">For every kind of business.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                        {INDUSTRIES.map((ind, i) => {
                            const Icon = CATEGORY_ICONS[ind.icon];
                            return (
                                <Reveal key={ind.name} delay={(i % 4) * 0.07}>
                                    <div className="glow-card group h-full rounded-xl p-5" data-testid={`industry-${ind.icon}`}>
                                        <Icon className="h-6 w-6 text-cyan-400 transition-transform group-hover:scale-110" />
                                        <h3 className="mt-3 font-display text-sm font-bold text-white">{ind.name}</h3>
                                        <p className="mt-1 text-xs leading-snug text-slate-500">{ind.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============ PLANS PREVIEW ============ */}
            <section id="plans" className="scroll-mt-20 py-6" data-testid="plans-preview">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto grid max-w-4xl gap-5 md:grid-cols-2">
                        {PLANS.map((p, i) => (
                            <Reveal key={p.id} delay={i * 0.1}>
                                <Link
                                    to="/pricing"
                                    data-testid={`plan-preview-${p.id}`}
                                    className={`glow-card group flex h-full flex-col rounded-2xl p-6 ${p.premium ? "border-amber-500/40" : ""} ${p.highlight ? "border-cyan-500/40" : ""}`}
                                >
                                    <span className={`mb-3 w-fit rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${p.premium ? "bg-amber-500/15 text-amber-300" : "bg-cyan-500/15 text-cyan-300"}`}>
                                        {p.id === "small" ? "For Small Business" : "For Enterprise Business"}
                                    </span>
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-display text-lg font-bold text-white">{p.name}</h3>
                                        {p.badge && (
                                            <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-emerald-300">
                                                {p.badge}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-3 flex items-end gap-1">
                                        <span className={`font-display text-3xl font-extrabold ${p.premium ? "amber-gradient-text" : "cyan-gradient-text"}`}>{p.price}</span>
                                        <span className="pb-1 text-xs text-slate-500">{p.period}</span>
                                    </div>
                                    <p className="mt-2 flex-1 text-xs leading-relaxed text-slate-400">{p.tagline}</p>
                                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-400 group-hover:text-cyan-300">
                                        View Plan <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ HOW IT WORKS — RECHARGE MODEL ============ */}
            <section className="relative py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>How It Works</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="journey-heading">
                            How WorkAssign AI <span className="cyan-gradient-text">works for you</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="steps-grid">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.1}>
                                <div className="glow-card relative h-full rounded-2xl p-6" data-testid={`journey-step-${s.n}`}>
                                    <div className="flex h-11 w-11 items-center justify-center rounded-full border border-cyan-500/40 bg-cyan-500/10 font-mono2 text-sm font-bold text-cyan-300">
                                        {s.n}
                                    </div>
                                    <h3 className="mt-4 font-display text-lg font-bold text-white">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                                    {i < 3 && <ArrowRight className="absolute -right-3.5 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-cyan-500/50 lg:block" />}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ SMALL BUSINESS EMPATHY ============ */}
            <section className="relative py-20 lg:py-28">
                <div className="glow-orb left-[15%] top-[10%] h-[300px] w-[400px] bg-amber-500/8" />
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>For Small Businesses</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="smallbiz-heading">
                            We Understand <span className="amber-gradient-text">Small Businesses.</span>
                        </h2>
                        <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-slate-400 sm:text-base">
                            Shop rent, electricity bills, staff salaries, stock purchases, supplier payments — the monthly
                            expenses never end. So why add one more fixed bill? If 10 customer enquiries come today, your AI
                            answers them all. If none come tomorrow, you're charged nothing.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid gap-6 md:grid-cols-3">
                        {HIGHLIGHTS.map((h, i) => (
                            <Reveal key={h.title} delay={i * 0.12}>
                                <div className="glow-card h-full rounded-2xl p-7" data-testid={`highlight-card-${i}`}>
                                    <div className={`mb-5 inline-flex rounded-xl p-3 ${h.cls}`}>
                                        <h.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="font-display text-xl font-bold text-white">{h.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{h.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2} className="mt-10 text-center">
                        <p className="mx-auto max-w-3xl font-display text-xl font-bold text-white sm:text-2xl" data-testid="small-biz-highlight">
                            No Enquiry. No Usage Charge. <span className="cyan-gradient-text">No Mandatory Monthly Bills.</span>
                        </p>
                        <p className="mx-auto mt-4 max-w-3xl font-display text-lg font-semibold leading-relaxed text-slate-300">
                            "We built AI to fit your pocket — <span className="cyan-gradient-text">not to become a burden on it.</span>"
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ============ RECHARGE DASHBOARD PREVIEW ============ */}
            <section className="relative overflow-hidden py-20 lg:py-28">
                <div className="glow-orb left-[5%] top-[20%] h-[300px] w-[300px] bg-emerald-500/10" />
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                    <Reveal>
                        <Eyebrow>Recharge Wallet</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="dashboard-heading">
                            Transparent spend. <span className="cyan-gradient-text">Total control.</span>
                        </h2>
                        <p className="mt-5 max-w-lg text-sm leading-relaxed text-slate-400 sm:text-base">
                            A simple dashboard for Small Business customers — wallet balance, remaining minutes/messages,
                            usage history and a clear record of every charge. On days with no billable usage, the charge
                            is zero.
                        </p>
                        <ul className="mt-7 space-y-3">
                            {["Wallet balance & recharge history", "Remaining calling minutes / messages", "Detailed usage charges — every call and message accounted", "No auto-recharge, no surprise bills"].map((t) => (
                                <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                                    <Check className="h-4 w-4 shrink-0 text-emerald-400" /> {t}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="glow-card rounded-3xl p-6 sm:p-7" data-testid="wallet-preview">
                            <div className="flex items-center justify-between">
                                <p className="font-display text-base font-bold text-white">Recharge Wallet</p>
                                <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-[10px] font-bold text-emerald-300" data-testid="wallet-zero-charge">
                                    No Usage Today — ₹0 Usage Charge
                                </span>
                            </div>
                            <div className="mt-5 grid grid-cols-3 gap-3">
                                <div className="rounded-xl border border-white/5 bg-[#111B30] p-3.5">
                                    <p className="font-mono2 text-[9px] uppercase tracking-wider text-slate-500">Wallet Balance</p>
                                    <p className="mt-1 font-display text-xl font-extrabold text-white">₹479.01</p>
                                </div>
                                <div className="rounded-xl border border-white/5 bg-[#111B30] p-3.5">
                                    <p className="font-mono2 text-[9px] uppercase tracking-wider text-slate-500">Call Minutes</p>
                                    <p className="mt-1 font-display text-xl font-extrabold text-cyan-300">48 / 50</p>
                                </div>
                                <div className="rounded-xl border border-white/5 bg-[#111B30] p-3.5">
                                    <p className="font-mono2 text-[9px] uppercase tracking-wider text-slate-500">AI Agents</p>
                                    <p className="mt-1 font-display text-xl font-extrabold text-emerald-300 notranslate">Veer · Sana</p>
                                </div>
                            </div>
                            <div className="mt-4 space-y-2">
                                {USAGE_ROWS.map((r) => (
                                    <div key={r.label} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.03] px-3.5 py-2.5">
                                        <div>
                                            <p className="text-xs font-medium text-slate-300">{r.label}</p>
                                            <p className="text-[10px] text-slate-600">{r.time}</p>
                                        </div>
                                        <span className={`font-mono2 text-xs font-bold ${r.credit ? "text-emerald-400" : "text-slate-400"}`}>{r.amount}</span>
                                    </div>
                                ))}
                            </div>
                            <Link
                                to="/pricing"
                                data-testid="wallet-recharge-button"
                                className="mt-5 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.01]"
                            >
                                <Wallet className="h-4 w-4" /> Recharge ₹499
                            </Link>
                            <p className="mt-3 text-center text-[10px] text-slate-600">Sample preview — actual dashboard reflects your real usage records.</p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ============ BENEFITS ============ */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>Scale Without Headcount</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="comparison-heading">
                            One AI employee handles <span className="cyan-gradient-text">thousands of conversations</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-6 md:grid-cols-2">
                        <Reveal>
                            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-7" data-testid="comparison-human">
                                <h3 className="font-display text-lg font-bold text-slate-300">Human Team Only</h3>
                                <ul className="mt-5 space-y-3.5">
                                    {["Limited to working hours", "Response depends on availability", "Follow-ups get forgotten", "Adding capacity means hiring"].map((t) => (
                                        <li key={t} className="flex items-center gap-3 text-sm text-slate-400">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500/10"><X className="h-3.5 w-3.5 text-red-400" /></span>
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="glow-card h-full rounded-2xl border-cyan-500/30 p-7 shadow-[0_0_40px_rgba(6,182,212,0.1)]" data-testid="comparison-ai">
                                <h3 className="font-display text-lg font-bold text-white">Human Team + <span className="cyan-gradient-text">AI Workforce</span></h3>
                                <ul className="mt-5 space-y-3.5">
                                    {["Always on — nights, weekends, holidays", "Every enquiry answered instantly", "Follow-ups happen automatically", "Scale conversations without new hires"].map((t) => (
                                        <li key={t} className="flex items-center gap-3 text-sm text-slate-200">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15"><Check className="h-3.5 w-3.5 text-emerald-400" /></span>
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ REVIEWS PREVIEW ============ */}
            <section className="relative py-20 lg:py-28" data-testid="reviews-preview">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>Customer Reviews</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="reviews-preview-heading">
                            Loved by businesses <span className="amber-gradient-text">across India</span>
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {FEATURED_REVIEWS.map((r, i) => (
                            <Reveal key={r.name} delay={i * 0.08}>
                                <article className="glow-card flex h-full flex-col rounded-2xl p-6" data-testid={`featured-review-${i}`}>
                                    <span className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map((s) => (
                                            <Star key={s} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                                        ))}
                                    </span>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">"{r.text}"</p>
                                    {r.translation && <p className="mt-2 text-[11px] italic leading-relaxed text-slate-500">{r.translation}</p>}
                                    <div className="mt-4 flex items-center gap-3">
                                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br font-display text-[11px] font-bold text-white ${r.gradient}`}>
                                            {r.name.split(" ").map((w) => w[0]).join("")}
                                        </div>
                                        <div>
                                            <p className="font-display text-xs font-bold text-white">{r.name}</p>
                                            <p className="flex items-center gap-1 text-[10px] text-slate-500">
                                                <MapPin className="h-2.5 w-2.5" /> {r.city} · {r.biz}
                                            </p>
                                        </div>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                    <div className="mt-10 text-center">
                        <Link
                            to="/reviews"
                            data-testid="reviews-preview-link"
                            className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-7 py-3 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/20"
                        >
                            Read All 1,248+ Reviews <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============ PR NEWS & MEDIA COVERAGE ============ */}
            <MediaCoverage />

            {/* ============ INQUIRY FORM ============ */}
            <section id="inquiry" className="relative scroll-mt-20 py-20 lg:py-28" data-testid="inquiry-section">
                <div className="glow-orb left-[8%] top-[15%] h-[320px] w-[320px] bg-cyan-500/10" />
                <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                    <Reveal>
                        <Eyebrow>Get Started</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="inquiry-heading">
                            Tell us about your business — <span className="cyan-gradient-text">we'll set up your AI employee</span>
                        </h2>
                        <p className="mt-5 max-w-lg text-sm leading-relaxed text-slate-400 sm:text-base">
                            Share your details and our team will call you back within 24 hours — with a live demo of
                            Veer & Sana trained on your business.
                        </p>
                        <ul className="mt-7 space-y-3.5">
                            {[
                                "Free consultation — no commitment",
                                "AI agents trained on your products & prices",
                                "Go live within a few days",
                            ].map((t) => (
                                <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15">
                                        <Check className="h-3.5 w-3.5 text-emerald-400" />
                                    </span>
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="glow-card rounded-3xl p-7 sm:p-9" data-testid="inquiry-card">
                            <LeadForm type="inquiry" testid="inquiry" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ============ FOUNDER QUOTE + CONVERSION CTA ============ */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
                    <Reveal>
                        <p className="font-display text-2xl font-bold leading-snug text-white sm:text-3xl" data-testid="founder-quote">
                            "You handle the business. <span className="cyan-gradient-text">Your AI handles the customers.</span>"
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="glow-card mt-14 rounded-3xl p-10 sm:p-14" data-testid="cta-banner">
                            <h2 className="font-display text-2xl font-extrabold leading-snug text-white sm:text-4xl">
                                Small business? Then your AI bill should <span className="amber-gradient-text">fit your needs too.</span>
                            </h2>
                            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
                                Some days bring a flood of customers, some days a trickle, some days none. That's why
                                WorkAssign AI frees you from a fixed monthly subscription — recharge as needed, pay only
                                for what you use.
                            </p>
                            <p className="mt-6 font-display text-xl font-bold text-white sm:text-2xl" data-testid="cta-payperuse">
                                Pay for What You Use. <span className="cyan-gradient-text">Not for What You Don't.</span>
                            </p>
                            <div className="mt-8 flex flex-wrap justify-center gap-4">
                                <Link
                                    to="/pricing"
                                    data-testid="cta-demo-button"
                                    className="animate-pulse-ring flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-8 py-3.5 text-sm font-semibold text-white"
                                >
                                    Start with ₹499 <ArrowRight className="h-4 w-4" />
                                </Link>
                                <a
                                    href="#demo"
                                    data-testid="cta-try-demo"
                                    className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
                                >
                                    Try AI Demo
                                </a>
                                <a
                                    href={COMPANY.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="cta-whatsapp-button"
                                    className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-8 py-3.5 text-sm font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/20"
                                >
                                    <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
